const POST_LOGIN_REDIRECT_SESSION_STORAGE_KEY_PREFIX = "oidc_post_login_redirect_path:";
const DISALLOWED_POST_LOGIN_REDIRECT_PATHS = ["/auth-callback", "/sign-in", "/sign-out"];

export const DEFAULT_POST_LOGIN_REDIRECT_PATH = "/u";
export const POST_LOGIN_REDIRECT_SEARCH_PARAM = "redirectTo";

type RouterLocationLike = {
    pathname: string;
    searchStr?: string;
    hash?: string;
};

export function getPostLoginRedirectPathFromLocation({
    location,
    intellsysBaseUrl,
}: {
    location: RouterLocationLike;
    intellsysBaseUrl: string;
}): string {
    return sanitizePostLoginRedirectPath([
        location.pathname,
        normalizeUrlPart(location.searchStr, "?"),
        normalizeUrlPart(location.hash, "#"),
    ].join(""), intellsysBaseUrl);
}

export function getPostLoginRedirectPathFromSearch({
    intellsysBaseUrl,
    search,
}: {
    intellsysBaseUrl: string;
    search: {redirectTo?: string | null | undefined};
}): string {
    return sanitizePostLoginRedirectPath(search.redirectTo, intellsysBaseUrl);
}

export function getPostLoginRedirectPathFromSearchString({
    intellsysBaseUrl,
    searchStr,
}: {
    intellsysBaseUrl: string;
    searchStr: string | undefined;
}): string {
    const searchParams = new URLSearchParams(searchStr ?? "");
    return sanitizePostLoginRedirectPath(searchParams.get(POST_LOGIN_REDIRECT_SEARCH_PARAM), intellsysBaseUrl);
}

export function storePostLoginRedirectPathForOidcState({
    intellsysBaseUrl,
    redirectPath,
    state,
}: {
    intellsysBaseUrl: string;
    redirectPath: string;
    state: string;
}): void {
    sessionStorage.setItem(
        getPostLoginRedirectPathSessionStorageKey(state),
        sanitizePostLoginRedirectPath(redirectPath, intellsysBaseUrl),
    );
}

export function consumePostLoginRedirectPathForOidcState({
    intellsysBaseUrl,
    state,
}: {
    intellsysBaseUrl: string;
    state: string;
}): string {
    const storageKey = getPostLoginRedirectPathSessionStorageKey(state);
    const redirectPath = sessionStorage.getItem(storageKey);
    sessionStorage.removeItem(storageKey);
    return sanitizePostLoginRedirectPath(redirectPath, intellsysBaseUrl);
}

function sanitizePostLoginRedirectPath(redirectPath: unknown, intellsysBaseUrl: string): string {
    if (typeof redirectPath !== "string") {
        return DEFAULT_POST_LOGIN_REDIRECT_PATH;
    }

    const trimmedRedirectPath = redirectPath.trim();
    if (
        trimmedRedirectPath === ""
        || trimmedRedirectPath.includes("\\")
    ) {
        return DEFAULT_POST_LOGIN_REDIRECT_PATH;
    }

    try {
        const neptuneUrl = new URL(intellsysBaseUrl);
        const redirectUrl = new URL(trimmedRedirectPath, neptuneUrl);
        if (redirectUrl.origin !== neptuneUrl.origin) {
            return DEFAULT_POST_LOGIN_REDIRECT_PATH;
        }
        if (isDisallowedPostLoginRedirectPath(redirectUrl.pathname)) {
            return DEFAULT_POST_LOGIN_REDIRECT_PATH;
        }

        return `${redirectUrl.pathname}${redirectUrl.search}${redirectUrl.hash}`;
    } catch {
        return DEFAULT_POST_LOGIN_REDIRECT_PATH;
    }
}

function normalizeUrlPart(value: string | undefined, prefix: "?" | "#"): string {
    if (value === undefined || value === "") {
        return "";
    }

    return value.startsWith(prefix) ? value : `${prefix}${value}`;
}

function isDisallowedPostLoginRedirectPath(pathname: string): boolean {
    return DISALLOWED_POST_LOGIN_REDIRECT_PATHS.some((disallowedPath) => (
        pathname === disallowedPath || pathname.startsWith(`${disallowedPath}/`)
    ));
}

function getPostLoginRedirectPathSessionStorageKey(state: string): string {
    return `${POST_LOGIN_REDIRECT_SESSION_STORAGE_KEY_PREFIX}${state}`;
}
